import { api } from "api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import {
  AddTaskGroupPayload,
  AddTaskItemPayload,
  CreateTaskBoardTemplatePayload,
  TaskBoardTemplateListItemDto,
  TaskBoardTemplateViewDto,
  TaskItemMember,
  TaskItemUpdatePayload,
} from "../dtos/tasks/task-template.dto";
import { T_MongoId } from "types/_utils.types";
import { Resource } from "dtos/stage.dto";
import { useDebounce } from "hooks/use-debounce";

// ─── Query Keys ───────────────────────────────────────────────────────────────

const TEMPLATE_KEY = "task-templates";

const templateListKey = (params: {
  orgCode?: T_MongoId;
  resource?: Resource;
  search?: string;
  page: number;
  limit: number;
}) => [TEMPLATE_KEY, "list", params] as const;

const templateDetailKey = (templateId?: T_MongoId | string) => [TEMPLATE_KEY, "detail", templateId] as const;

// ─── Service ──────────────────────────────────────────────────────────────────

export const taskTemplateService = {
  /**
   * Fetch a paginated list of task board templates.
   * Tasks array is excluded from each item — use `useGetById` for the full view.
   */
  useGetPaginated: (params: {
    page: number;
    limit: number;
    orgCode?: string;
    enabled?: boolean;
    resource?: Resource;
    search?: string;
  }) => {
    const debouncedSearch = useDebounce(params.search, 500);
    const resolvedParams = { ...params, search: debouncedSearch };

    return useQuery({
      queryKey: templateListKey(resolvedParams),
      enabled: params.enabled ?? true,
      queryFn: async () => {
        const res = await api.get(`/task/templates`, { params: resolvedParams });
        return res.data as T_Api_Success_Res<T_Api_Paginated<TaskBoardTemplateListItemDto>>;
      },
    });
  },

  /**
   * Fetch the full structured view of a single task board template,
   * including all groups and their ordered task trees.
   */
  useGetById: (templateId: string | undefined) => {
    return useQuery({
      queryKey: templateDetailKey(templateId),
      enabled: Boolean(templateId),
      queryFn: async () => {
        const res = await api.get(`/task/templates/${templateId}`);
        return res.data?.result as TaskBoardTemplateViewDto;
      },
    });
  },

  /**
   * Create a new task board template.
   * On success, invalidates the list cache.
   */
  useCreate: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: [TEMPLATE_KEY, "create"],
      mutationFn: async (payload: CreateTaskBoardTemplatePayload) => {
        const res = await api.post(`/task/templates`, payload);
        return res.data as T_Api_Success_Res<TaskBoardTemplateViewDto>;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [TEMPLATE_KEY, "list"] });
      },
    });
  },

  /**
   * Add one or more groups to an existing task board template.
   * On success, invalidates the detail cache for that template.
   */
  useAddGroup: (templateId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: [TEMPLATE_KEY, "add-group", templateId],
      mutationFn: async (payload: AddTaskGroupPayload[]) => {
        const res = await api.post(`/task/templates/${templateId}/groups`, payload);
        return res.data as T_Api_Success_Res<TaskBoardTemplateViewDto>;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: templateDetailKey(templateId) });
      },
    });
  },

  /**
   * Add a task item to a specific group.
   * On success, invalidates the detail cache so the task tree refreshes.
   */
  useAddTaskItem: (templateId: T_MongoId | undefined, groupId: T_MongoId | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: [TEMPLATE_KEY, "add-task", groupId],
      mutationFn: async (payload: AddTaskItemPayload) => {
        const res = await api.post(`/task/templates/groups/${groupId}/items`, payload);
        return res.data as T_Api_Success_Res<TaskBoardTemplateViewDto>;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: templateDetailKey(templateId) });
      },
    });
  },

  useUpdateTaskMembers: () => {
    return useMutation({
      mutationFn: async ({ taskId, member }: { taskId: T_MongoId; member: TaskItemMember }) => {
        const { data } = await api.patch<T_Api_Success_Res<unknown>>(`/task/templates/item/${taskId}/member`, member);
        return data.result;
      },
    });
  },

  useUpdateTaskDetails: () => {
    return useMutation({
      mutationFn: async ({ taskId, ...payload }: { taskId: T_MongoId } & TaskItemUpdatePayload) => {
        const { data } = await api.patch<T_Api_Success_Res<unknown>>(`/task/templates/item/${taskId}`, payload);
        return data.result;
      },
    });
  },
};
